import { StringFormatter } from '@repo/toolkit/string'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { PeopleTwoIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getPcr } from '../../api/pcr/get-pcr'
import { updatePcr } from '../../api/pcr/update-pcr'
import UserNavBar from '../../components/elements/nav-bar'
import PcrApplicationsTable from '../../components/pages/pcr/pcr-applications-table'
import PcrForm from '../../components/pages/pcr/pcr-form'
import { Paths } from '../../lib/constants/path'
import { PcrInput } from '../../lib/types/models/pcr/pcr-input'
import { updateButtonsConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'

const PcrUpdatePage = () => {
  const { id: pcrId } = useParams()
  const queryClient = useQueryClient()
  const { navigate, back } = useNavigator()

  const { data, isLoading } = useQuery({
    queryKey: ['pcr', pcrId],
    queryFn: () => {
      if (pcrId && !isNaN(parseInt(pcrId))) return getPcr(parseInt(pcrId))
    },
    enabled: !!pcrId
  })

  const { mutateAsync } = useMutation({
    mutationFn: updatePcr,
    onSuccess: () => {
      // Invalidate the pcr query to refetch the updated data
      queryClient.invalidateQueries({
        exact: true,
        queryKey: ['pcr', pcrId]
      })

      toast.success('PCR modifié avec succès')
    },
    onError: showAxiosError
  })

  const onSubmit = async (values: PcrInput) => {
    if (!pcrId) return

    await mutateAsync({ id: parseInt(pcrId), ...values })
  }

  if (!data || isLoading)
    return (
      <div className="flex justify-center items-center h-full">
        {isLoading ? <Loader /> : <Typography variant="h4">Invalid PCR ID</Typography>}
      </div>
    )

  return (
    <div>
      <UserNavBar
        title={`${data?.firstName ? StringFormatter.capitalize(data.firstName) : ''} ${data?.lastName ? data.lastName.toUpperCase() : ''} - ${data?.userId}`}
        icon={PeopleTwoIcon}
      />
      <div className="h-4" />
      <Box>
        <div className="flex justify-between items-center py-2">
          <div className="flex items-center gap-4">
            <Typography variant="sub-heading">
              Veuillez modifier le formulaire ci-dessous
            </Typography>
          </div>
        </div>
        <div className="h-5" />
        <PcrForm
          defaultValues={{
            ...data,
            email: data.user.email
          }}
          buttonsConfig={updateButtonsConfig({
            onCancel: () => back(Paths.PCRS_PAGE),
            onUpdate: onSubmit,
            onUpdateThenCreate: async (values) => {
              await onSubmit(values)
              navigate(Paths.PCR_CREATE_PAGE)
            },
            onUpdateThenList: async (values) => {
              await onSubmit(values)
              navigate(Paths.PCRS_PAGE, { replace: true })
            }
          })}
        />
      </Box>

      <div className="h-4" />
      {pcrId && !isNaN(parseInt(pcrId)) && <PcrApplicationsTable pcrId={parseInt(pcrId)} />}
    </div>
  )
}

export default PcrUpdatePage
